import React, { ReactElement, useRef } from 'react';
import {
  View, StyleSheet, Text, FlexStyle,
} from 'react-native';
import { useSelector } from 'react-redux';
import LottieView from 'lottie-react-native';
import PlusIcon from './16pxIcons/PlusIcon';
import Button from './Button';
import Colors from '../constants/Colors';
import isMobile from './IsMobile';

interface ScreenEmptyStateProps {
  image?: ReactElement,
  title?: string,
  subtitle?: string,
  buttonTitle?: string,
  buttonAction?: () => void,
  buttonTestID?: string,
  adminTitle?: string,
  adminSubtitle?: string,
  actionAvailable?: boolean,
  large?: boolean,
  style?: FlexStyle,
  buttonStyle?: FlexStyle,
  buttonIcon?: ReactElement,
}

const ScreenEmptyState = ({
  title,
  adminTitle,
  adminSubtitle,
  actionAvailable,
  buttonTitle,
  buttonAction,
  buttonTestID,
  large,
  style,
  buttonStyle,
  buttonIcon,
  image,
  subtitle,
}: ScreenEmptyStateProps) => {
  const animation = useRef<LottieView>(null);
  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);
  const { firstName } = useSelector((state: { user: { firstName } }) => state.user);
  let defaultTitle: string = 'Loading buildings';
  let defaultSubtitle = 'Fetching building data';
  if (notEmpty === null) {
    defaultTitle = 'Error loading buildings';
    defaultSubtitle = 'Unable to load building data at the moment';
  } else if (notEmpty === false) {
    defaultTitle = `Hi${firstName ? `, ${firstName}` : ' there'}!\nLet's get started!`;
    defaultSubtitle = `Before you can see any information, you will first need to set some data In here.${isMobile ? ' You can do it by adding the first building in the desktop version' : ''}`;
  }
  const displayedSubtitle: string = subtitle !== undefined ? subtitle : defaultSubtitle;

  // eslint-disable-next-line global-require
  const buildingLoader = require('../assets/animations/BuildingLoader.json');

  return <View style={[styles.container, style]}>
    <View style={styles.subContainer}>
      {image || <View style={{ alignSelf: 'stretch' }}><LottieView source={buildingLoader} style={{ width: '100%', height: 300 }} ref={animation} autoPlay loop /></View>}
      <Text style={[styles.title, large && styles.largeTitle]}>
        {(actionAvailable && adminTitle) ? adminTitle : title ?? defaultTitle}
      </Text>
      <Text style={[styles.subtitle, large && styles.largeSubtitle]}>
        {(actionAvailable && adminSubtitle) ? adminSubtitle : displayedSubtitle}
      </Text>
    </View>
    {actionAvailable && buttonTitle && buttonAction ? <Button
      title={`  ${buttonTitle}`}
      children={buttonIcon ?? <PlusIcon />}
      style={{ ...styles.button, ...buttonStyle }}
      affirmative
      onPress={buttonAction}
      testID={buttonTestID}
    /> : null}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingVertical: isMobile ? 20 : 80,
    flex: isMobile ? 1 : undefined,
    justifyContent: isMobile ? 'center' : undefined,
    paddingHorizontal: 24,
  },
  subContainer: {
    alignItems: 'center',
  },
  title: {
    marginTop: 32,
    marginBottom: 12,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    fontSize: 22,
    lineHeight: 33,
    textAlign: 'center',
  },
  largeTitle: {
    fontSize: 32,
    lineHeight: 48,
  },
  subtitle: {
    fontSize: 18,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 32,
    textAlign: 'center',
  },
  largeSubtitle: {
    fontSize: 16,
  },
  button: {
    marginStart: undefined,
    flex: undefined,
    flexDirection: 'row-reverse',
  },
});

export default ScreenEmptyState;
